import { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { fmtDateTime } from "../lib/format";
import {
  X, Loader2, FileText, Download, Send, Mail, Phone, MessageSquare, Paperclip,
  Plus, AlertTriangle, CheckCircle2,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * ReportPickerModal — shared Phase 3 component
 *
 * Usage modes:
 *   mode="inbox"   — picks/generates a report and sends it as attachment via email/whatsapp/sms
 *                    on the inbox thread. Requires `threadId` + `defaultRecipient`.
 *   mode="case"    — picks/generates a report and attaches it to a case's document vault.
 *                    Requires `caseId`. No send step.
 *   mode="client"  — quick generator that triggers right from the client homepage. No send step.
 *
 * In all modes the user can either:
 *   1. Pick an EXISTING report (from /api/reports?client_id=...)
 *   2. Click "Generate new" — opens the existing /reports builder modal in a new tab
 *
 *   (Inline generation is intentionally NOT here: the builder is a complex 4-step
 *    flow that already lives on /reports; we'd rather link out than reinvent it.)
 */
export default function ReportPickerModal({
  mode = "inbox", clientId, threadId, caseId,
  defaultRecipient, defaultChannel = "email", defaultSubject,
  onClose, onSuccess,
}) {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);

  // Send / attach state
  const [channel, setChannel] = useState(defaultChannel);
  const [recipient, setRecipient] = useState(defaultRecipient || "");
  const [subject, setSubject] = useState(defaultSubject || "");
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [recentRecipients, setRecentRecipients] = useState([]);

  // Load recent recipients for the active channel (inbox mode only)
  useEffect(() => {
    if (mode !== "inbox") return;
    api.get(`/reports/analytics/recent-recipients?channel=${channel}&limit=5`)
      .then((r) => setRecentRecipients(r.data || []))
      .catch(() => setRecentRecipients([]));
  }, [mode, channel]);

  useEffect(() => {
    setLoading(true);
    const q = clientId ? `?client_id=${clientId}` : "";
    api.get(`/reports${q}`).then((r) => setReports(r.data || []))
      .catch(() => setReports([]))
      .finally(() => setLoading(false));
  }, [clientId]);

  const selected = useMemo(() => reports.find((r) => r.id === selectedId), [reports, selectedId]);

  const submit = async () => {
    if (!selectedId) { setError("Pick a report first"); return; }
    setBusy(true); setError(null);
    try {
      if (mode === "case") {
        await api.post(`/reports/${selectedId}/attach-to-case`, { case_id: caseId, note: body || null });
        setSuccess("Attached to case · saved to case document vault.");
        setTimeout(() => onSuccess && onSuccess(selected), 800);
      } else if (mode === "inbox") {
        await api.post(`/reports/${selectedId}/send`, {
          channel,
          recipient,
          subject: subject || null,
          body: body || null,
          thread_id: threadId || null,
        });
        setSuccess(`Sent via ${channel} to ${recipient}.`);
        setTimeout(() => onSuccess && onSuccess(selected), 800);
      } else {
        // mode === "client" — quick download
        const token = localStorage.getItem("pm_token");
        const res = await fetch(`${API}/reports/${selectedId}/pdf`, { headers: { Authorization: `Bearer ${token}` } });
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a"); a.href = url; a.download = selected.filename || "report.pdf";
        document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
        setSuccess("Downloaded.");
        setTimeout(() => onSuccess && onSuccess(selected), 800);
      }
    } catch (e) {
      const detail = e?.response?.data?.detail;
      // 412 — agent hasn't connected Gmail/Outlook. Surface the Connect prompt
      // rather than a raw error.
      if (e?.response?.status === 412 && detail?.code === "email_not_configured") {
        setError({ kind: "email_not_configured", message: detail.message });
      } else if (typeof detail === "string") {
        setError(detail);
      } else if (detail?.message) {
        setError(detail.message);
      } else {
        setError(`Could not ${mode === "case" ? "attach" : "send"} report`);
      }
    } finally {
      setBusy(false);
    }
  };

  const openBuilder = () => {
    window.open("/reports", "_blank", "noopener");
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/70 backdrop-blur-sm flex items-center justify-center p-4" data-testid="report-picker-modal">
      <div className="bg-white w-full max-w-2xl rounded-2xl flex flex-col max-h-[calc(100vh-2rem)]">
        <div className="px-5 py-4 border-b border-slate-200 flex items-center gap-3">
          <Paperclip size={16} className="text-emerald-600" />
          <div className="flex-1">
            <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500">
              {mode === "case" ? "Attach to case" : mode === "inbox" ? "Send report" : "Pick a report"}
            </div>
            <div className="font-display text-lg font-bold tracking-tight">
              {mode === "case" ? "Pick a report to attach" : mode === "inbox" ? "Send a report" : "Player reports"}
            </div>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-lg hover:bg-slate-100 flex items-center justify-center" data-testid="picker-close-btn">
            <X size={14} />
          </button>
        </div>

        <div className="flex-1 p-5 overflow-y-auto space-y-4">
          {/* Existing reports */}
          <div>
            <div className="flex items-center gap-2 mb-2">
              <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500">Existing reports {clientId ? "for this player" : ""}</div>
              <button onClick={openBuilder} className="ml-auto pm-btn-secondary h-7 px-2.5 rounded-lg text-[10px] font-bold flex items-center gap-1" data-testid="picker-open-builder">
                <Plus size={10} /> Generate new
              </button>
            </div>
            {loading && <div className="text-slate-500 text-sm py-6 text-center"><Loader2 size={13} className="inline animate-spin mr-2" /> Loading reports…</div>}
            {!loading && reports.length === 0 && (
              <div className="rounded-xl border border-slate-200 px-4 py-6 text-center text-slate-500 text-[12px]">
                No reports yet. Click <b>Generate new</b> to build one in the report builder, then come back here to {mode === "case" ? "attach" : "send"} it.
              </div>
            )}
            {!loading && reports.length > 0 && (
              <div className="space-y-1.5 max-h-60 overflow-y-auto pr-1">
                {reports.slice(0, 30).map((r) => (
                  <button key={r.id} onClick={() => setSelectedId(r.id)}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl border text-left transition-all ${
                      selectedId === r.id ? "border-emerald-500 bg-emerald-50/40" : "border-slate-200 hover:border-emerald-300"
                    }`}
                    data-testid={`picker-report-${r.id}`}>
                    <div className="w-9 h-9 rounded-lg bg-slate-100 flex items-center justify-center shrink-0">
                      <FileText size={13} className="text-slate-500" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-bold truncate">{r.title || r.client_name || "Untitled"}</div>
                      <div className="text-[11px] text-slate-500 flex items-center gap-2">
                        <span className="capitalize">{(r.report_type || "").replace("_", " ")}</span>
                        <span>·</span>
                        <span className="capitalize">{r.theme}</span>
                        <span>·</span>
                        <span>{r.page_count}p</span>
                        <span>·</span>
                        <span>{fmtDateTime(r.created_at).slice(0, 11)}</span>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Send-mode fields */}
          {mode === "inbox" && selectedId && (
            <div className="rounded-xl border border-slate-200 p-4 space-y-3" data-testid="picker-send-form">
              <div className="flex items-center gap-2">
                {[
                  { id: "email",    label: "Email",   Icon: Mail },
                  { id: "whatsapp", label: "WhatsApp", Icon: MessageSquare },
                  { id: "sms",      label: "SMS",     Icon: Phone },
                ].map((c) => (
                  <button key={c.id} onClick={() => setChannel(c.id)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-sm font-bold ${
                      channel === c.id ? "border-emerald-500 bg-emerald-50 text-emerald-800" : "border-slate-200"
                    }`}
                    data-testid={`channel-${c.id}`}>
                    <c.Icon size={12} /> {c.label}
                  </button>
                ))}
              </div>
              <input value={recipient} onChange={(e) => setRecipient(e.target.value)}
                placeholder={channel === "email" ? "scouting@ajax.com" : "+316 12345678"}
                className="pm-input w-full text-[13px]" data-testid="picker-recipient-input" />
              {recentRecipients.length > 0 && (
                <div className="flex items-center gap-1.5 flex-wrap" data-testid="picker-recent-recipients">
                  <span className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Recent:</span>
                  {recentRecipients.map((rr) => (
                    <button key={rr.recipient} onClick={() => setRecipient(rr.recipient)}
                      className="px-2 py-0.5 rounded-md text-[11px] border border-slate-200 hover:border-emerald-400 hover:bg-emerald-50 text-slate-600 hover:text-emerald-700"
                      data-testid={`picker-recent-${rr.recipient}`}
                      title={`Sent ${rr.count}× · last ${rr.last_sent_at?.slice(0, 10)}`}>
                      {rr.recipient}
                    </button>
                  ))}
                </div>
              )}
              {channel === "email" && (
                <input value={subject} onChange={(e) => setSubject(e.target.value)}
                  placeholder={`Subject (default: ${selected?.title || selected?.client_name || "Player"} — Playmaker report)`}
                  className="pm-input w-full text-[13px]" data-testid="picker-subject-input" />
              )}
              <textarea value={body} onChange={(e) => setBody(e.target.value)}
                placeholder={channel === "email" ? "Optional cover note…" : "Optional message body…"}
                rows={3} className="pm-input w-full text-[13px]" data-testid="picker-body-input" />
            </div>
          )}

          {mode === "case" && selectedId && (
            <div className="rounded-xl border border-slate-200 p-4">
              <textarea value={body} onChange={(e) => setBody(e.target.value)}
                placeholder="Optional note attached to the case document entry"
                rows={2} className="pm-input w-full text-[13px]" data-testid="picker-case-note" />
            </div>
          )}

          {error && (
            error.kind === "email_not_configured" ? (
              <div className="rounded-xl bg-amber-50 border border-amber-200 p-3" data-testid="email-not-configured-banner">
                <div className="flex items-start gap-2">
                  <AlertTriangle size={14} className="text-amber-700 mt-0.5 shrink-0" />
                  <div>
                    <div className="text-[13px] font-bold text-amber-900">No email account connected</div>
                    <div className="text-[12px] text-amber-800 mt-1">{error.message}</div>
                    <div className="flex items-center gap-2 mt-3">
                      <a href="/settings/integrations?connect=gmail" className="pm-btn-secondary h-8 px-3 rounded-lg text-[11px] font-bold" data-testid="connect-gmail-btn">Connect Gmail</a>
                      <a href="/settings/integrations?connect=outlook" className="pm-btn-secondary h-8 px-3 rounded-lg text-[11px] font-bold" data-testid="connect-outlook-btn">Connect Outlook</a>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-[12px] text-rose-700 flex items-center gap-1.5"><AlertTriangle size={11} /> {typeof error === "string" ? error : error.message || "Error"}</div>
            )
          )}
          {success && <div className="text-[12px] text-emerald-700 flex items-center gap-1.5"><CheckCircle2 size={11} /> {success}</div>}
        </div>

        <div className="px-5 py-3 border-t border-slate-200 flex items-center justify-end gap-2">
          <button onClick={onClose} className="pm-btn-secondary h-9 px-3 rounded-lg text-[12px] font-bold" data-testid="picker-cancel-btn">Cancel</button>
          <button onClick={submit} disabled={busy || !selectedId}
            className="pm-btn-primary h-9 px-4 rounded-lg text-[12px] font-bold flex items-center gap-1.5 disabled:opacity-30 disabled:cursor-not-allowed"
            data-testid="picker-submit-btn">
            {busy ? <Loader2 size={11} className="animate-spin" /> : (mode === "client" ? <Download size={11} /> : mode === "case" ? <Paperclip size={11} /> : <Send size={11} />)}
            {busy ? "Working…" : mode === "client" ? "Download" : mode === "case" ? "Attach to case" : `Send via ${channel}`}
          </button>
        </div>
      </div>
    </div>
  );
}
